import { Badge, Box, Divider, IconButton, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useRecoilValue } from 'recoil';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { settingsAtom } from '../../atoms/Settings';
import { useThemeColor } from '../../hooks/useThemeColor';
import { SearchBox } from '../SearchBox/SearchBox';
import { sharedColors } from '../../utils/Style';
import { NavMenuItem } from '../../interfaces/Settings/NavMenuItem';
import { useClickMenuItem } from './useClickMenuItem';
import { useBasket } from '../../hooks/useBasket';
import { isTokenValid } from '../../utils/Auth';

type MenuAccordionProps = {
  menuItem: NavMenuItem;
};

const SecondLevelAccordion = ({ menuItem }: MenuAccordionProps) => {
  const [open, setOpen] = useState(false);

  const onClick = useClickMenuItem();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        onClick={() => {
          if (menuItem.items.length) {
            setOpen((x) => !x);
          } else {
            onClick(menuItem);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 2.5,
          py: 1,
          justifyContent: 'space-between',
        }}
      >
        <Typography
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            onClick(menuItem);
          }}
          sx={{ fontSize: '14px', fontWeight: 700, color: sharedColors.gray6 }}
        >
          {menuItem.name}
        </Typography>

        {!!menuItem.items.length && (
          <IconButton
            size='small'
            sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>

      {open &&
        menuItem.items.map((child, i) => (
          <Box
            key={i}
            sx={{
              px: 2.5,
              py: 1,
            }}
          >
            <Typography
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();

                onClick(child);
              }}
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: sharedColors.gray6,
              }}
            >
              {child.name}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

const TopLevelAccordion = ({ menuItem }: MenuAccordionProps) => {
  const [open, setOpen] = useState(false);

  const onClick = useClickMenuItem();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        onClick={() => {
          if (menuItem.items.length) {
            setOpen((x) => !x);
          } else {
            onClick(menuItem);
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Typography
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();

            onClick(menuItem);
          }}
          sx={{ fontSize: '16px', fontWeight: 600, color: sharedColors.gray7 }}
        >
          {menuItem.name}
        </Typography>

        {!!menuItem.items.length && (
          <IconButton
            sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Box>

      <Divider />

      {open &&
        menuItem.items.map((child, i) => (
          <SecondLevelAccordion key={i} menuItem={child} />
        ))}
    </Box>
  );
};

export const AppHeaderMobile = () => {
  const settings = useRecoilValue(settingsAtom);
  const [showMenu, setShowMenu] = useState(false);

  const { primary, secondary } = useThemeColor();

  const { totalQuantity } = useBasket();

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setShowMenu(false);
  }, [location.pathname]);

  const logo = settings.settingsV1.storefront_header_logo;
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
        }}
      >
        <IconButton onClick={() => setShowMenu((x) => !x)}>
          <MenuIcon />
        </IconButton>

        {logo ? (
          <Box
            component='img'
            onClick={() => navigate('')}
            src={logo}
            height={55}
            width='auto'
          />
        ) : (
          <Typography>{settings.settingsV1.storefront_navbar_text}</Typography>
        )}

        <Box sx={{ ml: 'auto' }} />

        <Box>
          <IconButton onClick={() => navigate('/favoriler')}>
            <FavoriteBorderIcon />
          </IconButton>

          <IconButton onClick={() => navigate('/sepet')}>
            <Badge
              badgeContent={totalQuantity.toString()}
              sx={{
                '& .MuiBadge-badge': {
                  color: secondary,
                  backgroundColor: primary,
                },
              }}
              overlap='circular'
              style={{ transform: 'translate(25px, -15px)' }}
            />
            <ShoppingBasketIcon />
          </IconButton>

          <IconButton
            onClick={() => navigate(isTokenValid() ? '/account' : '/giris-yap')}
          >
            <PersonOutlineIcon />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <Box
        p={1.5}
        sx={{
          backgroundColor: showMenu ? sharedColors.white : sharedColors.gray3,
        }}
      >
        <SearchBox fullWidth placeholder='Aramak için yazınız...' />
      </Box>

      {showMenu && (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
          {settings.menu.map((item, i) => (
            <TopLevelAccordion key={i} menuItem={item} />
          ))}
        </Box>
      )}
    </Box>
  );
};
